import { CreditCardData } from 'data/shared/CreditCardData';
import {
  CardRewardsEarnRate,
  CardData,
  SelectableCategoriesEarnRate,
} from 'data/types/CardData';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import {
  isSelectableCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  isQuarterlyCategoriesEarnRate,
} from 'data/types/utils';
import { AdvancedCategory } from 'data/types';
import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { getSelectedCategoriesForRate } from '../data/shared/hooks/useBestEarnRatesV2';

/**
 * Result of finding a matching earn rate.
 */
export type MatchedEarnRate = {
  rate: CardRewardsEarnRate;
  /** User's category selections for this specific rate (empty if not a selectable rate) */
  userSelections: string[][];
};

/**
 * Check if a category string matches a reward category.
 * Handles hierarchical categories like "travel.airline" matching "travel".
 */
const categoryMatches = (
  targetCategory: string,
  rewardCategory: string | AdvancedCategory
): boolean => {
  const categoryStr =
    typeof rewardCategory === 'string'
      ? rewardCategory
      : rewardCategory.category_description;

  return (
    categoryStr === targetCategory ||
    targetCategory.startsWith(`${categoryStr}.`) ||
    categoryStr.startsWith(`${targetCategory}.`)
  );
};

/**
 * Check if a reward applies to a given category.
 * Handles selectable categories, fixed category groups, and quarterly categories.
 */
const rewardAppliesToCategory = (
  reward: CardRewardsEarnRate,
  category: string
): boolean => {
  // Direct category match
  if (categoryMatches(category, reward.category)) {
    return true;
  }

  // Check selectable categories
  if (
    isSelectableCategoriesEarnRate(reward) ||
    isFixedCategoryGroupEarnRate(reward)
  ) {
    const categories = reward.selectableCategories?.categories || [];
    return categories.some((cat) => categoryMatches(category, cat));
  }

  // Check quarterly categories
  if (isQuarterlyCategoriesEarnRate(reward)) {
    const categories = reward.selectableCategories?.categories || [];
    return categories.some((cat) => {
      if (typeof cat === 'string') {
        return categoryMatches(category, cat);
      }
      // AdvancedCategory - check inner categories
      const advCat = cat as AdvancedCategory;
      return advCat.categories?.some((innerCat) =>
        categoryMatches(category, innerCat)
      );
    });
  }

  return false;
};

/**
 * Find the rate index among selectable rates for a given reward.
 * Returns -1 if the reward is not a selectable rate.
 */
const findSelectableRateIndex = (
  allRates: CardRewardsEarnRate[],
  reward: CardRewardsEarnRate
): number => {
  if (!isSelectableCategoriesEarnRate(reward)) {
    return -1;
  }

  const selectableRates = allRates.filter((r) =>
    isSelectableCategoriesEarnRate(r)
  ) as SelectableCategoriesEarnRate[];

  return selectableRates.findIndex((r) => r === reward);
};

/**
 * Get user selections for a specific rate index.
 */
const getUserSelectionsForRate = (
  cardSelections: string[][][] | undefined,
  rateIndex: number
): string[][] => {
  if (!cardSelections || rateIndex < 0) {
    return [];
  }
  return cardSelections[rateIndex] || [];
};

/**
 * Find the CardRewardsEarnRate that corresponds to an EarnRateInfo.
 *
 * This looks up the raw earn rate data from CreditCardData that matches
 * the computed best rate for a category.
 *
 * @param earnRateInfo - The computed earn rate info
 * @param cardSelections - Optional user category selections for this card (indexed by rate)
 * @returns The matching earn rate with user selections, or null if not found
 */
export function findMatchingEarnRate(
  earnRateInfo: EarnRateInfo,
  cardSelections?: string[][][]
): MatchedEarnRate | null {
  const cardData = CreditCardData[earnRateInfo.card];
  if (!cardData?.rewards_earn_rates) {
    return null;
  }

  // Find rewards that apply to this category
  const matchingRewards = cardData.rewards_earn_rates.filter((reward) =>
    rewardAppliesToCategory(reward, earnRateInfo.category)
  );

  if (matchingRewards.length === 0) {
    return null;
  }

  let matchedRate: CardRewardsEarnRate;

  if (matchingRewards.length === 1) {
    matchedRate = matchingRewards[0];
  } else {
    // Multiple matches - try to find the one with matching earn rate
    const exactMatch = matchingRewards.find(
      (reward) => reward.earnRate === earnRateInfo.earnRate
    );
    if (exactMatch) {
      matchedRate = exactMatch;
    } else {
      // Check if any reward has promotions that match the earn rate
      const promoMatch = matchingRewards.find((reward) => {
        if (!reward.promotions) {
          return false;
        }
        return reward.promotions.some(
          (p) => p.earnRate === earnRateInfo.earnRate
        );
      });
      matchedRate = promoMatch || matchingRewards[0];
    }
  }

  const rateIndex = findSelectableRateIndex(
    cardData.rewards_earn_rates,
    matchedRate
  );
  const userSelections = getUserSelectionsForRate(cardSelections, rateIndex);

  return {
    rate: matchedRate,
    userSelections,
  };
}

/**
 * Find the earn rate that applies to a specific category for a card.
 *
 * @param cardOrId - Either a CreditCard ID or CardData object
 * @param category - The category to find the rate for
 * @param cardSelections - Optional user category selections for this card (indexed by rate)
 * @returns The matching earn rate with user selections, or null if not found
 */
export function findEarnRateForCategory(
  cardOrId: CreditCard | CardData,
  category: string,
  cardSelections?: string[][][]
): MatchedEarnRate | null {
  // Get the card data
  const cardData: CardData | undefined =
    typeof cardOrId === 'string'
      ? CreditCardData[cardOrId as CreditCard]
      : cardOrId;

  if (!cardData?.rewards_earn_rates) {
    return null;
  }

  const allRates = cardData.rewards_earn_rates;

  // Find the earn rate that applies to this category
  for (const rate of allRates) {
    // Skip rewards that require the travel portal (consistent with useBestEarnRatesV2)
    if (rate.requiresTravelPortal) {
      continue;
    }

    // Direct category match
    if (rate.category === category) {
      const rateIndex = findSelectableRateIndex(allRates, rate);
      return {
        rate,
        userSelections: getUserSelectionsForRate(cardSelections, rateIndex),
      };
    }

    // Check selectable categories
    if (isSelectableCategoriesEarnRate(rate)) {
      const rateIndex = findSelectableRateIndex(allRates, rate);

      if (rateIndex >= 0) {
        const { applicableCategories } = getSelectedCategoriesForRate({
          userSelectedCategoriesForCard: cardSelections || [],
          reward: rate,
          rateIndex,
        });

        if (applicableCategories.includes(category)) {
          return {
            rate,
            userSelections: getUserSelectionsForRate(cardSelections, rateIndex),
          };
        }
      }
    }

    // Check fixed category groups
    if (isFixedCategoryGroupEarnRate(rate)) {
      if (
        rate.selectableCategories.categories.some((c) =>
          typeof c === 'string'
            ? c === category
            : c.categories.includes(category)
        )
      ) {
        return {
          rate,
          userSelections: [], // Fixed groups don't have user selections
        };
      }
    }

    // Check quarterly categories
    if (isQuarterlyCategoriesEarnRate(rate)) {
      if (
        rate.selectableCategories.categories.some((c) =>
          typeof c === 'string'
            ? c === category
            : (c as AdvancedCategory).categories?.includes(category)
        )
      ) {
        return {
          rate,
          userSelections: [], // Quarterly categories don't have user selections
        };
      }
    }
  }

  return null;
}

/**
 * Find matching earn rates for multiple EarnRateInfo objects.
 *
 * @param earnRateInfos - Array of computed earn rate infos
 * @returns Map of category to CardRewardsEarnRate
 */
export function findMatchingEarnRates(
  earnRateInfos: EarnRateInfo[]
): Map<string, CardRewardsEarnRate> {
  const result = new Map<string, CardRewardsEarnRate>();

  for (const info of earnRateInfos) {
    const match = findMatchingEarnRate(info);
    if (match) {
      result.set(info.category, match.rate);
    }
  }

  return result;
}
